/**
 * ============================================================================
 *  BinOccupancyPage.jsx  —  v4 (mockup-faithful)
 * ----------------------------------------------------------------------------
 *  Matches the bin occupancy layout from CSOps_Mockup_AllTabs_v2.html exactly:
 *
 *    • Single chamber selector (CS-1 OR CS-2) via dropdown
 *    • 5-stat strip: Total · Available · Occupied · Reserved · Blocked
 *    • Legend
 *    • 2-column grid of dark slate rack cards (8 racks per chamber)
 *    • Each rack: 15 columns (A1-E3) or 17 columns for racks 1 & 8 (F1,F2 extra)
 *    • Each column: 6 levels (bottom = 1, top = 6) rendered as colored cells
 *    • Click any bin → details panel on the right (300px sticky)
 *    • Bin code format: `{rack}{col}-{level}` (e.g. "1A2-3"); col = letter+digit
 *
 *  Status interpretation:
 *    • Bin appears in inventory.cs1 / cs2 with in-stock status → OCCUPIED (red)
 *    • Bin NOT in inventory                                    → AVAILABLE (green)
 *    • Reserved / Blocked: 0 for now (no schema support yet; both colors live in
 *      the legend ready for future reservation / quarantine workflows)
 *
 *  Receives `shift` prop from OperationsContainer (informational only;
 *  bin occupancy is shift-agnostic since pallets stay put regardless of shift).
 * ============================================================================
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { fetchCSOpsBinOccupancy, fetchCSOpsStockPallets, todayISO, yesterdayISO } from '../utils/csOpsApi';
import { exportPalletList, PALLET_EXPORT_COLUMNS } from '../utils/csOpsExport';
import './BinOccupancyPage.css';

const fmtN = (n) => Number(n || 0).toLocaleString();

/* ---------------------------------------------------------------------------
 *  Static grid definition — matches mockup exactly
 * ------------------------------------------------------------------------- */
const RACKS = [1, 2, 3, 4, 5, 6, 7, 8];
const LEVELS_TOP_TO_BOTTOM = [6, 5, 4, 3, 2, 1];

function getRackColumns(rackNumber) {
  const base = ['A1','A2','A3','B1','B2','B3','C1','C2','C3','D1','D2','D3','E1','E2','E3'];
  if (rackNumber === 1 || rackNumber === 8) return ['F1', 'F2', ...base];
  return base;
}

// Total bin count per chamber: 6×15×6 + 2×17×6 = 540 + 204 = 744
const TOTAL_BINS_PER_CHAMBER = (() => {
  let total = 0;
  RACKS.forEach(r => { total += getRackColumns(r).length * LEVELS_TOP_TO_BOTTOM.length; });
  return total;
})();

/* ---------------------------------------------------------------------------
 *  Single rack card
 * ------------------------------------------------------------------------- */
function RackCard({ rackNumber, occupiedMap, selectedBinCode, onSelectBin }) {
  const cols = getRackColumns(rackNumber);

  // Insert a section gap whenever the leading letter changes (A→B, B→C, etc.)
  const colsWithGaps = [];
  let prevSection = '';
  cols.forEach(c => {
    const section = c[0];
    if (prevSection && prevSection !== section) colsWithGaps.push({ gap: true });
    colsWithGaps.push({ col: c });
    prevSection = section;
  });

  return (
    <div className="csbin-rack-card">
      <div className="csbin-rack-header">
        🗄 Rack {rackNumber}
        <span className="meta">({cols.length} cols × 6 levels)</span>
      </div>
      <div className="csbin-rack-grid">
        {/* Level labels column */}
        <div className="csbin-col-group">
          <div className="csbin-col-header">LVL</div>
          {LEVELS_TOP_TO_BOTTOM.map(l => (
            <div key={l} className="csbin-level-label">{l}</div>
          ))}
        </div>

        {colsWithGaps.map((item, i) => {
          if (item.gap) return <div key={'g' + i} className="csbin-section-gap" />;
          const col = item.col;
          return (
            <div key={col} className="csbin-col-group">
              <div className="csbin-col-header">{col}</div>
              {LEVELS_TOP_TO_BOTTOM.map(level => {
                const dbCode = `${rackNumber}${col}-${level}`;
                const bin = occupiedMap[dbCode];
                const status = bin ? 'occupied' : 'available';
                const isSel = selectedBinCode === dbCode;
                const title = bin
                  ? `${dbCode}\n${bin.pallets} pal · ${fmtN(bin.cartons)} ctn\n${bin.product || '?'}`
                  : `${dbCode} (available)`;
                return (
                  <div
                    key={level}
                    className={'csbin-cell ' + status + (isSel ? ' csbin-selected' : '')}
                    title={title}
                    onClick={() => onSelectBin({
                      binCode: dbCode, rackNumber, col, level, status, bin
                    })}
                  >
                    {level}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------------------
 *  Details panel (right side, sticky)
 * ------------------------------------------------------------------------- */
function DetailsPanel({ selected, chamberPrefix }) {
  return (
    <div className="csbin-details-panel">
      <div className="csbin-details-title">📍 Bin Details</div>
      {selected ? (
        <>
          <div className="csbin-details-bin-box">
            <div className="lbl">Bin Code</div>
            <div className="code">{chamberPrefix}-{selected.binCode}</div>
          </div>
          <div className="csbin-details-row">
            <span className="key">Status</span>
            <span className={
              'csbin-pill ' + (
                selected.status === 'available' ? 'csbin-green' :
                selected.status === 'occupied'  ? 'csbin-red'   :
                selected.status === 'reserved'  ? 'csbin-amber' : 'csbin-gray'
              )
            }>
              {selected.status.toUpperCase()}
            </span>
          </div>
          <div className="csbin-details-row">
            <span className="key">Rack</span>
            <span className="val">Rack {selected.rackNumber}</span>
          </div>
          <div className="csbin-details-row">
            <span className="key">Position</span>
            <span className="val mono">{selected.col} · Level {selected.level}</span>
          </div>
          {selected.bin && (
            <>
              <div className="csbin-details-row">
                <span className="key">Pallets</span>
                <span className="val mono">{fmtN(selected.bin.pallets)}</span>
              </div>
              <div className="csbin-details-row">
                <span className="key">Cartons</span>
                <span className="val mono">{fmtN(selected.bin.cartons)}</span>
              </div>
              <div className="csbin-details-row">
                <span className="key">Weight</span>
                <span className="val mono">{fmtN(selected.bin.kgs)} kg</span>
              </div>
              <div className="csbin-details-row">
                <span className="key">Product</span>
                <span className="val">{selected.bin.product || '—'}</span>
              </div>
              <div className="csbin-details-row">
                <span className="key">Brand</span>
                <span className="val">{selected.bin.brand || '—'}</span>
              </div>
              <div className="csbin-details-row">
                <span className="key">Grade</span>
                <span className="val">{selected.bin.grade || '—'}</span>
              </div>
              <div className="csbin-details-row">
                <span className="key">Oldest produced</span>
                <span className="val mono">{selected.bin.oldest_production || '—'}</span>
              </div>
              {selected.bin.mixed_products && (
                <div className="csbin-details-row">
                  <span className="key">⚠ Mixed</span>
                  <span className="val">Multiple products in this bin</span>
                </div>
              )}
            </>
          )}
          {!selected.bin && (
            <div className="csbin-details-empty-state">
              Bin is empty and ready to receive pallets.
            </div>
          )}
        </>
      ) : (
        <div className="csbin-details-empty-state">
          Click any bin to see its details.
        </div>
      )}
    </div>
  );
}

/* ============================================================================
 *  MAIN COMPONENT
 * ============================================================================ */
export default function BinOccupancyPage({ shift = 'ALL' }) {
  // ---- State ----
  const [chamber,  setChamber]  = useState('CS-1');   // 'CS-1' | 'CS-2'
  const [asOfMode, setAsOfMode] = useState('Today');
  const [asOfDate, setAsOfDate] = useState(todayISO());
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [selected, setSelected] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  // ---- Excel export state ----
  const [exporting, setExporting] = useState(false);

  // ---- Loader ----
  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const cs = chamber === 'CS-1' ? 'CS1' : 'CS2';
      const res = await fetchCSOpsBinOccupancy(asOfDate, cs);
      if (!mountedRef.current) return;
      if (res.success) {
        setData(res.data);
        setLastUpdated(new Date());
      } else {
        setError(res.error || 'Failed to load');
      }
    } catch (e) {
      if (mountedRef.current) setError(e.message);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [asOfDate, chamber]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const id = setInterval(load, 60_000);
    return () => clearInterval(id);
  }, [load]);

  // ---- As-of preset chips ----
  const setAsOfPreset = (mode) => {
    setAsOfMode(mode);
    if (mode === 'Today')     setAsOfDate(todayISO());
    if (mode === 'Yesterday') setAsOfDate(yesterdayISO());
  };
  const handleDate = (d) => {
    setAsOfDate(d);
    if (d === todayISO())          setAsOfMode('Today');
    else if (d === yesterdayISO()) setAsOfMode('Yesterday');
    else                            setAsOfMode('Custom');
  };

  // ---- Excel export — FULL pallet list across BOTH chambers (18 columns) [v8] ----
  //
  // Always exports both CS-1 and CS-2 in a single sheet. This makes the export's
  // totals match the Operations Dashboard's drilldown (which is also both-chamber).
  // The user can filter the "Cold Store" column inside Excel to slice by chamber.
  const handleExport = async () => {
    if (exporting) return;
    setExporting(true);
    setError('');
    try {
      const res = await fetchCSOpsStockPallets(asOfDate, {}, { cs: 'ALL', limit: 50000 });
      if (!mountedRef.current) return;
      if (!res?.success) {
        setError(res?.error || 'Export failed');
        return;
      }
      const pallets = res.data?.pallets || [];
      if (pallets.length === 0) {
        setError('No pallets to export.');
        return;
      }
      // Use distinct_pallets (matches dashboard KPI semantics) [v8]
      const distinct = res.data.totals.distinct_pallets ?? res.data.totals.pallet_count;
      await exportPalletList({
        filename:   `bin_occupancy_all_chambers_asof_${asOfDate}`,
        sheetName:  'Bin Occupancy (CS-1 + CS-2)',
        rows:       pallets,
        columns:    PALLET_EXPORT_COLUMNS,
        summaryRows: [
          ['CSOps · Bin Occupancy · Full Pallet Export · BOTH CHAMBERS'],
          ['As of',   asOfDate],
          ['Pallets (distinct)', distinct,
           'Inventory rows',     res.data.totals.pallet_count,
           'Cartons',            res.data.totals.cartons,
           'Kgs',                res.data.totals.kgs],
          ['Note', 'Filter the "Cold Store" column to view a single chamber'],
          ['Generated', new Date().toLocaleString()],
          res.data.truncated ? ['⚠ Truncated', `Showing first ${res.data.limit} of more pallets`] : [],
          [],
        ],
      });
    } catch (e) {
      if (mountedRef.current) setError('Export error: ' + e.message);
    } finally {
      if (mountedRef.current) setExporting(false);
    }
  };

  // ---- Lookup map: bin_code → bin info ----
  const occupiedMap = useMemo(() => {
    const map = {};
    const csKey = chamber === 'CS-1' ? 'cs1' : 'cs2';
    if (data?.[csKey]?.bins) {
      data[csKey].bins.forEach(b => { map[b.bin_code] = b; });
    }
    return map;
  }, [data, chamber]);

  // ---- Stats: count over the full canonical grid ----
  const counts = useMemo(() => {
    let occupied = 0;
    RACKS.forEach(r => {
      const cols = getRackColumns(r);
      cols.forEach(col => {
        LEVELS_TOP_TO_BOTTOM.forEach(level => {
          if (occupiedMap[`${r}${col}-${level}`]) occupied += 1;
        });
      });
    });
    const total = TOTAL_BINS_PER_CHAMBER;
    return {
      total,
      occupied,
      available: total - occupied,
      reserved: 0,    // no schema for these yet
      blocked:  0,
    };
  }, [occupiedMap]);

  const chamberPrefix = chamber === 'CS-1' ? 'CS1' : 'CS2';

  return (
    <div className="csbin-root">
      {/* Title bar */}
      <div className="csbin-title-bar">
        <div>
          <h2>📍 Bin Occupancy</h2>
          <div className="csbin-meta">
            Visual view · Click any bin to see details · {TOTAL_BINS_PER_CHAMBER} bins per chamber
            {lastUpdated && ` · Last updated ${lastUpdated.toLocaleTimeString()}`}
          </div>
        </div>
        <div className="csbin-title-controls">
          {/* As-of toggle */}
          <div className="csbin-asof-toggle">
            {['Today', 'Yesterday', 'Custom'].map(opt => (
              <button key={opt}
                className={asOfMode === opt ? 'active' : ''}
                onClick={() => setAsOfPreset(opt)}>{opt}</button>
            ))}
            <input type="date" value={asOfDate}
                   onChange={e => handleDate(e.target.value)} />
          </div>
          <button className="csbin-icon-btn csbin-export-btn"
                  onClick={handleExport}
                  disabled={exporting || !data}
                  title="Download full pallet list (both chambers) as Excel">
            {exporting ? 'Exporting…' : '📥 Excel Export'}
          </button>
          <button className="csbin-icon-btn" onClick={load} disabled={loading}>
            {loading ? '…' : '↻ Refresh'}
          </button>
        </div>
      </div>

      {/* Chamber selector bar */}
      <div className="csbin-chamber-bar">
        <label>Select Chamber</label>
        <select value={chamber} onChange={e => { setChamber(e.target.value); setSelected(null); }}>
          <option value="CS-1">CS-1 — Cold Store 1</option>
          <option value="CS-2">CS-2 — Cold Store 2</option>
        </select>
        <div className="csbin-chamber-utilization">
          {counts.total > 0 && (
            <>
              <span className="mono">{Math.round((counts.occupied / counts.total) * 100)}%</span>
              <span> utilization · {counts.occupied}/{counts.total} bins</span>
            </>
          )}
        </div>
      </div>

      {error && <div className="csbin-error-banner">⚠ {error}</div>}

      {/* Stats strip */}
      <div className="csbin-stats">
        <div className="csbin-stat total">
          <div className="csbin-num total">{fmtN(counts.total)}</div>
          <div className="csbin-lbl">Total Bins</div>
        </div>
        <div className="csbin-stat available">
          <div className="csbin-num available">{fmtN(counts.available)}</div>
          <div className="csbin-lbl">Available</div>
        </div>
        <div className="csbin-stat occupied">
          <div className="csbin-num occupied">{fmtN(counts.occupied)}</div>
          <div className="csbin-lbl">Occupied</div>
        </div>
        <div className="csbin-stat reserved">
          <div className="csbin-num reserved">{fmtN(counts.reserved)}</div>
          <div className="csbin-lbl">Reserved</div>
        </div>
        <div className="csbin-stat blocked">
          <div className="csbin-num blocked">{fmtN(counts.blocked)}</div>
          <div className="csbin-lbl">Blocked</div>
        </div>
      </div>

      {/* Legend */}
      <div className="csbin-legend">
        <span className="key">Legend:</span>
        <div className="item"><div className="swatch available" /> Available</div>
        <div className="item"><div className="swatch occupied" /> Occupied</div>
        <div className="item"><div className="swatch reserved" /> Reserved</div>
        <div className="item"><div className="swatch blocked" /> Blocked</div>
      </div>

      {/* Bin grid + details */}
      <div className="csbin-bins-layout">
        <div className="csbin-racks-grid">
          {RACKS.map(r => (
            <RackCard key={r}
                      rackNumber={r}
                      occupiedMap={occupiedMap}
                      selectedBinCode={selected?.binCode}
                      onSelectBin={setSelected} />
          ))}
        </div>
        <DetailsPanel selected={selected} chamberPrefix={chamberPrefix} />
      </div>
    </div>
  );
}
